import {
  ProposalCoverageStatusEnum,
  ProposalStatusEnum,
  type Proposal,
} from "@platform/mpx-quotes-grid";

/** How often the proposals query re-polls while a carrier response is outstanding. */
export const AWAITING_CARRIER_QUOTES_REFETCH_MS = 10_000;

const isAwaitingCarrierQuote = (proposal: Proposal): boolean =>
  proposal.origin === "carrier" &&
  (proposal.status === ProposalStatusEnum.PENDING ||
    proposal.coverages.some(
      (coverage) => coverage.status === ProposalCoverageStatusEnum.PENDING,
    ));

/**
 * True while any invited carrier's API response is still outstanding — its
 * proposal shell is pending, or a coverage within it is neither quoted nor
 * declined yet. Carrier responses land server-to-server with no client-side
 * completion event (unlike document uploads, whose extraction status is
 * polled), so the Quotes page keeps the proposals query polling while this
 * holds and stops once every carrier has answered.
 */
export const hasAwaitingCarrierQuotes = (
  proposals: readonly Proposal[] | undefined,
): boolean => !!proposals?.some(isAwaitingCarrierQuote);

/**
 * The proposals query's poll interval: every AWAITING_CARRIER_QUOTES_REFETCH_MS
 * while a carrier quote is outstanding, and never while a plan group move is
 * pending. A poll that lands during a move still holds the pre-move page and
 * would put the moved column back on its old page until the move finishes.
 */
export const proposalsPollInterval = (
  proposals: readonly Proposal[] | undefined,
  isMovingPlanGroup: boolean,
): number | false =>
  !isMovingPlanGroup && hasAwaitingCarrierQuotes(proposals)
    ? AWAITING_CARRIER_QUOTES_REFETCH_MS
    : false;
